import React, { useEffect, useState } from 'react';
import {
  Typography,
  Row,
  Col,
  Spin,
  Alert,
  Empty,
  Card,
  Tag,
  Button,
  Popconfirm,
} from 'antd';
import {
  HistoryOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/redux';
import { apiClient, Brand } from '../services/api';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

interface HistoryBrand extends Brand {
  visited_at: string;
  visit_count: number;
  duration?: number;
}

const HistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector(state => state.auth);
  const { t } = useTranslation();

  const [history, setHistory] = useState<HistoryBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    loadHistory();
  }, [isAuthenticated, navigate]);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.getBrowsingHistory(20);
      setHistory(response.data as HistoryBrand[]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg || t('history.loading_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = async () => {
    try {
      await apiClient.clearBrowsingHistory();
      setHistory([]);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      setError(msg || t('history.clear_failed'));
    }
  };

  const handleViewDetails = (id: number) => {
    navigate(`/brands/${id}`);
  };

  const formatVisitTime = (visitedAt: string) => {
    const date = new Date(visitedAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return t('history.days_ago', { count: diffDays });
    } else if (diffHours > 0) {
      return t('history.hours_ago', { count: diffHours });
    } else {
      return t('history.just_now');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2}>
          <HistoryOutlined
            style={{ color: 'var(--app-primary-color)', marginRight: 8 }}
          />
          {t('history.title')}
        </Title>

        {history.length > 0 && (
          <Popconfirm
            title={t('history.confirm_clear_title')}
            onConfirm={handleClearHistory}
            okText={t('history.confirm_ok')}
            cancelText={t('history.confirm_cancel')}
          >
            <Button danger icon={<DeleteOutlined />}>
              {t('history.clear_history')}
            </Button>
          </Popconfirm>
        )}
      </div>

      {error && (
        <Alert
          message={t('history.loading_failed')}
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {history.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('history.empty_desc')}
          style={{ marginTop: 48 }}
        >
          <Button type="primary" onClick={() => navigate('/brands')}>
            {t('history.discover')}
          </Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {history.map(brand => (
            <Col span={8} key={`${brand.id}-${brand.visited_at}`}>
              <Card
                hoverable
                onClick={() => handleViewDetails(brand.id)}
                style={{ cursor: 'pointer' }}
                actions={[
                  <Button
                    key="view"
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => handleViewDetails(brand.id)}
                  >
                    {t('history.view_details')}
                  </Button>,
                ]}
              >
                <Card.Meta
                  title={brand.name}
                  description={
                    <div>
                      <p style={{ marginBottom: 8 }}>{brand.description}</p>
                      <p style={{ marginBottom: 8, color: '#666' }}>
                        {t('favorites.founded_short')}:{' '}
                        {new Date(brand.founded).getFullYear()} |{' '}
                        {t('favorites.headquarters_short')}:{' '}
                        {brand.headquarters}
                      </p>
                      {(() => {
                        const overall = (
                          brand as unknown as {
                            overall_score?: number;
                          }
                        ).overall_score;
                        return typeof overall === 'number';
                      })() && (
                        <div style={{ marginBottom: 8 }}>
                          <Text strong>{t('favorites.ethical_score')}: </Text>
                          <Text
                            style={{
                              color:
                                (brand as unknown as { overall_score?: number })
                                  .overall_score! >= 4
                                  ? '#52c41a'
                                  : (
                                        brand as unknown as {
                                          overall_score?: number;
                                        }
                                      ).overall_score! >= 3
                                    ? '#faad14'
                                    : '#ff4d4f',
                            }}
                          >
                            {(
                              brand as unknown as { overall_score?: number }
                            ).overall_score!.toFixed(1)}
                            /5
                          </Text>
                        </div>
                      )}
                      <div style={{ marginBottom: 8 }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {formatVisitTime(brand.visited_at)} •{' '}
                          {t('history.visited_times', {
                            count: brand.visit_count,
                          })}
                          {brand.duration &&
                            brand.duration > 0 &&
                            ` • ${t('history.stayed_seconds', { seconds: brand.duration })}`}
                        </Text>
                      </div>
                      <div>
                        {brand.categories.map(category => (
                          <Tag
                            key={category}
                            style={{
                              color: 'var(--app-primary-color)',
                              borderColor: 'var(--app-primary-color)',
                              backgroundColor: 'transparent',
                              marginBottom: 4,
                            }}
                          >
                            {category}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default HistoryPage;
